import { Workspace, type WorkspaceProps } from './Workspace'
import DataLoader from 'dataloader'
import { type CursorPaged } from '@/infra/paging'
import { Id } from '@/infra/id'
import { type IamApi } from '@/features/iam/api/iam-api'
import { collection } from '@/infra/collection'
import { mapWorkspaceResourceToProps } from './workspace-mappers'
import type { PrimeWorkspaceProps } from '@/features/iam'

/**
 * Sets a workspace from its props, unless it has been fetched already.
 */
export type PrimeFullWorkspaceFn = (props: Partial<WorkspaceProps>) => Workspace

export interface ListWorkspacesQuery {
  searchText?: string
  after?: string | null
  limit?: number
}

/**
 * Manages workspaces.
 */
export class WorkspaceStore {
  private readonly _workspaceLoaderCache = new Map()

  readonly workspaces = collection({
    getDataId: (data) => data.id,
    getModelId: (model) => model.id,
    create: () => new Workspace(),
  })

  private readonly _workspaceLoader = new DataLoader<string, Workspace | null>(
    this._fetchMany.bind(this),
    {
      cacheMap: this._workspaceLoaderCache,
    },
  )

  constructor(private readonly _api: IamApi) {}

  /**
   * Gets a workspace by ID or slug from the internal collection.
   * @param id
   */
  getWorkspaceByIdOrSlug(id: string | undefined): Workspace | undefined {
    if (!id) {
      return undefined
    }
    return (
      this.workspaces.find((x) => x.slug === id || x.id === id) ?? undefined
    )
  }

  /**
   * Primes the cache with the workspace if we don't already have one.
   */
  primeFullWorkspace(props: Partial<WorkspaceProps>): Workspace {
    if (this._workspaceLoaderCache.has(props.id)) {
      return this.workspaces.set({ id: props.id })
    }

    const workspace = this.workspaces.set(props)
    this._workspaceLoader.prime(Id.assert(props.id), workspace)
    return workspace
  }

  /**
   * Fetches the workspace if it hasn't been done already.
   * Returns immediately.
   */
  primeWorkspace(props: PrimeWorkspaceProps): Workspace | undefined {
    if (!props.id) {
      return undefined
    }

    void this.fetchWorkspace(props.id)
    return this.workspaces.set({ id: props.id })
  }

  /**
   * Fetches a workspace by ID, batched and cached.
   */
  async fetchWorkspace(
    id: Id,
    options?: FetchWorkspaceOptions,
    signal?: AbortSignal,
  ): Promise<Workspace | null> {
    const workspaceId = Id.assert(id)
    if (options?.force) {
      this._workspaceLoader.clear(workspaceId)
    }

    const workspace = await this._workspaceLoader.load(workspaceId)
    signal?.throwIfAborted()
    return workspace
  }

  /**
   * Fetches a workspace by ID or slug, bypassing the cache.
   */
  async fetchWorkspaceByIdOrSlug(idOrSlug: Id, signal?: AbortSignal) {
    const resource = await this._api.workspaces.get(Id.assert(idOrSlug), signal)
    return resource
      ? this.workspaces.set(mapWorkspaceResourceToProps(resource))
      : null
  }

  /**
   * Makes the current platform admin a member of a workspace.
   */
  async joinAsAdmin(id: Id): Promise<void> {
    await this._api.platformAdmin.joinWorkspaceAsAdmin(Id.assert(id))
  }

  /**
   * Makes the current platform admin the owner of a workspace.
   */
  async promoteToOwner(id: Id): Promise<void> {
    await this._api.platformAdmin.promoteToOwner(Id.assert(id))
  }

  /**
   * Lists workspaces matching the query.
   */
  async listWorkspaces(
    query: ListWorkspacesQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Workspace>> {
    const response = await this._api.workspaces.list(
      { searchText: query.searchText, after: query.after, limit: query.limit },
      signal,
    )
    return {
      data: this.workspaces.set(response.data.map(mapWorkspaceResourceToProps)),
      next: response.cursor,
      total: response.total,
    }
  }

  private async _fetchMany(ids: readonly string[]) {
    const dtos = await this._api.workspaces.getMany(ids)
    dtos.forEach((dto, i) => {
      const id = ids[i]
      if (!dto && id) {
        this._workspaceLoader.clear(id)
      }
    })
    return this.workspaces.set(
      dtos.map((dto) => (dto ? mapWorkspaceResourceToProps(dto) : dto)),
    )
  }
}

export interface FetchWorkspaceOptions {
  /**
   * Whether to clear the cached loader entry before fetching.
   */
  force?: boolean
}
